/**
 * SalesLeads.tsx — Sales / Inbound Leads: the pre-pipeline queue (§8.F).
 *
 * VOCABULARY (§8.A, held in every label here): "Inbound Leads" is this
 * queue — unconverted early-funnel contacts. "New Lead" is the first
 * PIPELINE stage — a deal. Converting a queued lead creates a deal that
 * enters the pipeline at New Lead; the queue never calls itself "New
 * Leads".
 *
 * QUEUE PREDICATE (L8-2, Klemen 2026-08-22): status 'new' AND
 * converted=false — BOTH server-side (fetchInboundLeads/leadsQueueParams;
 * never a client filter of the capped list). A converted lead is a deal now
 * and must not appear; Mark contacted / Disqualify exit the queue too.
 *
 * List styling mirrors SalesArchive (the house closr-table precedent);
 * columns per §8.F: Name, Company, Source (badge), Status, Assigned,
 * Received. The "New Lead" button re-presents the Contacts create drawer
 * (§8.B — manual intake IS lead intake under L8-1); a created contact is
 * born status-new, so the queue refetches to include it.
 *
 * D3 (Slice D, §15.15 D-4): Status and Assigned are inline picklists. Status
 * offers the queue's own EXITS (Contacted / Unqualified — the two the lead
 * detail page writes; `qualified` is convert's to write, §8.D); Assigned
 * offers current employees. Both write the live contacts PUT (a lead IS a
 * contact) and each committed change is a permanent field-history row. A
 * status exit takes the row OUT of the queue at once — the L8-2 predicate is
 * the server's and a row that no longer satisfies it must not be shown — and
 * the toast says so, so nothing vanishes silently.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchInboundLeads, fetchDirectory, updateLead } from '../../api'
import { NewContactDrawer } from './SalesContacts'
import { InlinePicklist } from './InlinePicklist'
import {
  LEAD_STATUS_OPTIONS, ownerOptions, ownerLabel, ownerCellText, statusLabel, statusChange, ownerChange,
  inLeadsQueue, changeSummary,
} from './inlineEdit'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { statusContact } from '../../../../theme/tokens'
import type { SalesLead } from '../../types'

// The queue lists status-new leads only (L8-2), but the map covers the full
// vocabulary so a mid-session status write can re-render honestly.
const STATUS_CONFIG: Record<SalesLead['status'], { label: string; bg: string; text: string }> = {
  new:         { label: 'New',         ...statusContact.new },
  contacted:   { label: 'Contacted',   ...statusContact.contacted },
  qualified:   { label: 'Qualified',   ...statusContact.qualified },
  unqualified: { label: 'Unqualified', ...statusContact.unqualified },
}

/** Source as a neutral pill — the wire's free-text value verbatim ('—' when
 *  empty); no invented taxonomy. */
function SourceBadge({ source }: { source: string }) {
  if (source === '') return <span className="text-ink-faint">—</span>
  return (
    <span className="k-badge bg-surface-panel text-ink-secondary">
      {source.toUpperCase()}
    </span>
  )
}

export function SalesLeads({ onSelectLead }: { onSelectLead?: (id: string) => void }) {
  // reloadKey bumps after a drawer create so the queue re-fetches — the
  // server row is authoritative (no optimistic prepend of a view-mapped copy).
  const [reloadKey, setReloadKey] = useState(0)
  const [showNewDrawer, setShowNewDrawer] = useState(false)
  const { data, loading, error } = useAsync(fetchInboundLeads, [reloadKey])
  // D3: an inline write's SERVER response overrides the fetched row by id
  // (the lead detail's override precedent) — derived at render, never
  // mirrored into state from the fetch. Cleared on every refetch.
  const [overrides, setOverrides] = useState<Record<string, SalesLead>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  // One directory read backs every row's owner picklist — never per row.
  const directory = useAsync(() => fetchDirectory(), [])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  // The L8-2 predicate re-applied to the overridden rows: a lead whose status
  // an inline edit just changed is no longer in the queue and is not drawn.
  const leads = (data ?? []).map(l => overrides[l.id] ?? l).filter(inLeadsQueue)
  const people = ownerOptions(directory.data)
  const peopleText = directory.loading ? 'Loading people…' : directory.error ? 'People unavailable — owner unchanged' : 'No current employees'

  // D3.3: the PUT fires on the option click; the row keeps its STORED value
  // (marked "Saving…") until the server answers; the server's record then
  // replaces it — on failure nothing changes, because nothing optimistic was
  // ever drawn, and the toast carries the server's message.
  async function commitInline(l: SalesLead, field: 'status' | 'owner', body: { status: string } | { assignedEmployeeId: string }) {
    setBusyId(l.id)
    try {
      const updated = await updateLead(l.id, body)
      setOverrides(o => ({ ...o, [updated.id]: updated }))
      const from = field === 'status' ? statusLabel(l.status) : ownerLabel(l)
      const to = field === 'status' ? statusLabel(updated.status) : ownerLabel(updated)
      const gone = !inLeadsQueue(updated)
      toast.success(`${changeSummary(l.name, field, from, to)} · recorded in change history${gone ? ' · left the queue — no longer an inbound lead' : ''}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, `${l.name}: ${field} not changed`))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Inbound Leads"
        subtitle={`${leads.length} in the queue · converting a lead creates a pipeline deal`}
        action={
          <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowNewDrawer(true)}>
            <Plus size={14} /> New Lead
          </button>
        }
      />

      <div className="k-card">
        {leads.length === 0 ? (
          <EmptyState
            message="No inbound leads in the queue"
            hint="New leads land here. Converting one creates a deal that enters the pipeline at the New Lead stage."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table sales-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Company</th>
                  <th className="text-left">Source</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Assigned</th>
                  <th className="text-left">Received</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(l => {
                  const cfg = STATUS_CONFIG[l.status]
                  return (
                    <tr key={l.id} className="cursor-pointer" onClick={() => onSelectLead?.(l.id)}>
                      <td>
                        <div className="flex items-center gap-3">
                          <Avatar name={l.name} size={28} />
                          <div>
                            <div className="text-13 font-semibold text-ink-primary">{l.name}</div>
                            <div className="text-11 text-ink-faint">{l.email || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-13 text-ink-body">{l.company || '—'}</td>
                      <td><SourceBadge source={l.source} /></td>
                      <td>
                        <InlinePicklist value={l.status} options={LEAD_STATUS_OPTIONS} label={`Change status for ${l.name}`}
                          busy={busyId === l.id}
                          onCommit={v => { const body = statusChange(l.status, v); if (body) void commitInline(l, 'status', body) }}>
                          <span className="k-badge" style={{ background: cfg.bg, color: cfg.text }}>
                            <span className="k-badge-dot" />{cfg.label.toUpperCase()}
                          </span>
                        </InlinePicklist>
                      </td>
                      <td>
                        <InlinePicklist value={l.assignedEmployeeId ?? ''} options={people} emptyText={peopleText}
                          label={`Change owner for ${l.name}`} busy={busyId === l.id}
                          onCommit={v => { const body = ownerChange(l.assignedEmployeeId, v); if (body) void commitInline(l, 'owner', body) }}>
                          <span className={`text-12 ${l.assignedTo ? 'text-ink-secondary' : 'text-ink-faint'}`}>{ownerCellText(l)}</span>
                        </InlinePicklist>
                      </td>
                      <td className="text-12 text-ink-secondary">{l.createdLabel}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNewDrawer && (
        <NewContactDrawer
          onClose={() => setShowNewDrawer(false)}
          onCreated={() => { setOverrides({}); setReloadKey(k => k + 1) }}
        />
      )}
    </div>
  )
}
